import React, {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useSelector, useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import {
  Row, Col, Popover,
  Radio, Badge,
} from 'antd';
import { useLocation, useHistory } from 'react-router-dom';
import { DateTime } from 'luxon';

import SideListSearchHeader from '../common/SideListSearchHeader';
import SideList from '../common/SideList';
import FilterDropdown from '../common/FilterDropdown';
import OnTraccrButton from '../common/buttons/OnTraccrButton';
import OnTraccrRadioGroup from '../common/inputs/OnTraccrRadioGroup';

import TimeCardsContainer from './TimeCardsContainer';
import ArchivedUserWarning from './ArchivedUserWarning';

import { filterData as archiveFilterData } from '../teams/teamFilters';

import { search, mergeSets, getIdMap } from '../helpers/helpers';
import Permissions from '../auth/Permissions';

import TimeTracking from '../state/timeTracking';

import { getTimesFromDisplayTimeRange } from './timecard.helpers';

import { updateCurrentTimeEntriesRange } from './state/timecards.actions';

const DEFAULT_VIEW = 'default';
const SUBMISSION_VIEW = 'submitted';
const ACTIVE_FILTER_KEY = 'active';
const TEAM_FILTER_KEY = 'teams';
const DEFAULT_ACTIVE_FILTER = new Set([1]);
const EMPTY_SET = new Set();

const styles = {
  submitRadioGroup: {
    width: '100%',
    display: 'flex',
    borderRadius: 6,
    overflow: 'hidden',
  },
  submitRadio: {
    textAlign: 'center',
    border: 'none',
  },
  submitBadge: {
    marginLeft: 8,
    marginBottom: 4,
    height: 20,
    lineHeight: '20px',
    boxShadow: 'none',
  },
};

const getUserIdKey = (userList) => userList.map(({ id }) => id).sort().join(',');

const renderItem = (item) => {
  const name = item?.name?.length > 27 ? `${item.name.substr(0, 25)}...` : (item?.name || '');
  // Only users have an active flag, the summary row does not
  if (item?.active !== false && item?.active !== 0) return name;
  return (
    <Row type="flex" align="middle" style={{ width: '100%' }}>
      {name}
      <ArchivedUserWarning />
    </Row>
  );
};

function TeamTimeCardContainer({
  onUserSelect: propUserSelect,
}) {
  const dispatch = useDispatch();
  const history = useHistory();
  const {
    state: { submissions: showSubmissions, user: locationStateUser } = {},
  } = useLocation();

  const approvalCount = useSelector((state) => state.approvals.approvalCount);
  const settings = useSelector((state) => state.settings.company?.settings);
  const selectedDivisions = useSelector((state) => state.settings.selectedDivisions);
  const divisions = useSelector((state) => state.settings.divisions);
  const users = useSelector((state) => state.users.users);
  const teams = useSelector((state) => state.teams.teams);
  const approvals = useSelector((state) => state.approvals.approvals);
  const isFullScreen = useSelector((state) => state.main.menuCollapsed);
  const {
    timeRange,
  } = useSelector((state) => state.timecards);

  const [selectedSubmitView, setSelectedSubmitView] = useState(DEFAULT_VIEW);
  const [selectedUser, setSelectedUser] = useState();
  const [searchTerm, setSearchTerm] = useState();
  const [loadingId, setLoadingId] = useState(false);
  // No teams checked means users are not filtered by team
  const [activeFilters, setActiveFilters] = useState({
    [ACTIVE_FILTER_KEY]: DEFAULT_ACTIVE_FILTER,
  });

  const {
    enableTimecardSubmission,
  } = settings;

  const onSearch = useCallback((e) => {
    const {
      target: {
        value,
      } = {},
    } = e;
    setSearchTerm(value);
  }, []);

  const onFilter = useCallback((checkedFilters, filterCategory) => {
    setActiveFilters((oldFilters) => ({
      ...oldFilters,
      [filterCategory]: new Set(checkedFilters),
    }));
  }, []);

  const isApprovals = useMemo(() => selectedSubmitView === SUBMISSION_VIEW, [selectedSubmitView]);
  const approvalSet = useMemo(() => new Set(approvals.map(({ taskId }) => taskId)), [approvals]);
  const submissionSet = useMemo(() => new Set(approvals.map(({ userId }) => userId)), [approvals]);

  const {
    ourTeams: parsedTeams,
    userToTeamMap: userTeamMap,
  } = useMemo(() => {
    const ourTeams = [];
    const userToTeamMap = {};

    teams.forEach((team) => {
      if (!team.active || team?.divisionIds?.every((dId) => !selectedDivisions.has(dId))) return;
      const {
        members = [],
      } = team;

      members.forEach((member) => {
        const {
          id: userId,
          teamRole,
        } = member;

        if (!userToTeamMap[userId]) {
          userToTeamMap[userId] = [];
        }

        userToTeamMap[userId].push({
          teamId: team.id,
          role: teamRole,
        });
      });

      ourTeams.push({
        ...team,
        memberSet: new Set(members.map((member) => member.id)),
      });
    });

    return {
      ourTeams,
      userToTeamMap,
    };
  }, [teams, selectedDivisions]);

  const doesSuperviseUser = useCallback((userId) => {
    const userTeams = userTeamMap[userId];
    if (!userTeams) return false;
    const currentUserTeams = userTeamMap[Permissions.id];
    if (!currentUserTeams) return false;

    const currentUserTeamMap = getIdMap(currentUserTeams, 'teamId');

    return userTeams.some(({ teamId, role }) => (
      currentUserTeamMap[teamId]?.role === 'Supervisor' && role !== 'Supervisor'
    ));
  }, [userTeamMap]);

  const selectedTeamSet = activeFilters[TEAM_FILTER_KEY] ?? EMPTY_SET;

  /**
   * Set of user ids belonging to the checked teams.
   * Null when no team is checked, meaning users are not filtered by team.
   */
  const selectedTeamMemberSet = useMemo(() => {
    if (selectedTeamSet.size === 0) return null;
    const selectedTeams = parsedTeams.filter(({ id }) => selectedTeamSet.has(id));
    return mergeSets(selectedTeams.map(({ memberSet }) => memberSet));
  }, [parsedTeams, selectedTeamSet]);

  const fullUsers = useMemo(() => {
    const activeFilterSet = activeFilters[ACTIVE_FILTER_KEY] ?? EMPTY_SET;
    const userDivSets = mergeSets(
      Object.values(divisions)
        .filter(({ id: divisionId }) => selectedDivisions.has(divisionId))
        .map(({ users: divUsers }) => divUsers),
    );

    const hasPermissionForUser = (user) => (
      (Permissions.id !== user.id && Permissions.has(`TIMECARD_VIEW_${Permissions.formatPosition(user.position)}`))
      || (Permissions.id === user.id && Permissions.has('TIMECARD_VIEW_SELF'))
      || doesSuperviseUser(user.id)
    );

    const usersWithAccess = users.filter((user) => (
      activeFilterSet.has(user.active ? 1 : 0)
      && userDivSets.has(user.id)
      && (!selectedTeamMemberSet || selectedTeamMemberSet.has(user.id))
      && hasPermissionForUser(user)
    ));

    return usersWithAccess;
  }, [
    activeFilters,
    selectedTeamMemberSet,
    divisions,
    selectedDivisions,
    users,
    doesSuperviseUser,
  ]);

  const relevantUsers = useMemo(() => (
    fullUsers?.filter((user) => (
      selectedSubmitView === DEFAULT_VIEW || submissionSet.has(user.id)
    )) ?? []
  ), [fullUsers, selectedSubmitView, submissionSet]);

  const relevantUsersWithSummary = useMemo(() => (
    [{ name: 'Summary', id: 'summary' }].concat(relevantUsers)
  ), [relevantUsers]);

  const relevantUserKey = useMemo(() => getUserIdKey(relevantUsers), [relevantUsers]);

  const filteredUsers = useMemo(() => (
    search({
      data: relevantUsersWithSummary,
      value: searchTerm,
      getField: (user) => user.name,
    })
  ), [relevantUsersWithSummary, searchTerm]);

  // Tracks the users the summary time entries were last fetched for
  const loadedSummaryUserKey = useRef();

  const loadTime = useCallback(({
    newUser,
    newRange = null,
    newSubmitView = null,
    newRelevantUsers = null,
  }) => {
    if (!newUser?.id || newUser.id === loadingId) return;
    const isSummary = newUser?.id === 'summary';
    const payload = { getRunningTasks: true };

    if (isSummary) {
      const relevantUsersToUse = newRelevantUsers ?? relevantUsers;
      if (!relevantUsersToUse?.length) return;
      payload.userIds = relevantUsersToUse.map((u) => u.id);
      loadedSummaryUserKey.current = getUserIdKey(relevantUsersToUse);
    } else {
      payload.userId = newUser.id;
    }

    const timeRangeToUse = newRange ?? timeRange;
    let finalIsApprovals = isApprovals;
    if (newSubmitView === SUBMISSION_VIEW) {
      finalIsApprovals = true;
    } else if (newSubmitView === DEFAULT_VIEW) {
      finalIsApprovals = false;
    }
    if (!finalIsApprovals) {
      const { startTime: ourStart, endTime: ourEnd } = getTimesFromDisplayTimeRange(timeRangeToUse);
      payload.startTime = ourStart;
      payload.endTime = ourEnd;
    } else {
      payload.ids = Array.from(approvalSet);
      payload.removeIds = true;
    }

    payload.getCardDetails = true;

    setLoadingId(newUser.id);
    dispatch(TimeTracking.getTimeEntries(payload));
    if (payload.startTime && payload.endTime) {
      dispatch(updateCurrentTimeEntriesRange(
        DateTime.fromMillis(payload.startTime),
        DateTime.fromMillis(payload.endTime),
      ));
    }
    setLoadingId(false);
  }, [isApprovals, relevantUsers, approvalSet, loadingId, timeRange]);

  const onSubmitViewChange = useCallback(async (view) => {
    setSelectedSubmitView(view);
    const newRelevantUsers = fullUsers?.filter((user) => (
      view === SUBMISSION_VIEW ? submissionSet.has(user.id) : true
    )) ?? [];
    await loadTime({
      newUser: selectedUser,
      newSubmitView: view,
      newRelevantUsers,
    });
  }, [selectedUser, fullUsers, submissionSet, loadTime]);

  const onUserSelected = useCallback(async (user) => {
    const {
      name,
    } = user;
    await loadTime({ newUser: user });
    propUserSelect(name);
    setSelectedUser(user);
  }, [propUserSelect, loadTime]);

  const onDateRangeChange = useCallback((newRange) => {
    loadTime({ newUser: selectedUser, newRange });
  }, [selectedUser, loadTime]);

  useEffect(() => {
    if (
      // Wait for relevant users to be loaded
      relevantUsersWithSummary.length > 1
      && (
        !selectedUser
        || !relevantUsersWithSummary.some((user) => user.id === selectedUser.id)
      )
    ) {
      onUserSelected(relevantUsersWithSummary[0]);
    }
  }, [relevantUsersWithSummary, selectedUser, onUserSelected]);

  useEffect(() => {
    if (
      selectedUser?.id !== 'summary'
      || !relevantUsers.length
      || loadedSummaryUserKey.current === relevantUserKey
    ) return;
    loadTime({ newUser: selectedUser });
  }, [selectedUser, relevantUsers, relevantUserKey, loadTime]);

  useEffect(() => {
    if (locationStateUser) {
      const ourUser = relevantUsers.find((user) => user.id === locationStateUser);
      if (ourUser) {
        onUserSelected(ourUser);
        setSelectedSubmitView(showSubmissions ? SUBMISSION_VIEW : DEFAULT_VIEW);
        history.replace({
          pathname: '/timecards/team',
          state: {},
        });
      }
    }
  }, [locationStateUser, showSubmissions, onUserSelected, relevantUsers]);

  const filters = useMemo(() => ([
    [[
      {
        title: 'Teams',
        key: TEAM_FILTER_KEY,
        children: parsedTeams.map(({ id, name }) => ({ title: name, key: id })),
      },
    ]],
    archiveFilterData,
  ]), [parsedTeams]);

  const filterIsAtDefault = useMemo(() => {
    const activeFilterSet = activeFilters[ACTIVE_FILTER_KEY] ?? EMPTY_SET;
    return activeFilterSet.size === 1
      && activeFilterSet.has(1)
      && selectedTeamSet.size === 0;
  }, [activeFilters, selectedTeamSet]);

  const showSubmissionHeader = Permissions.has('TEAMS_WRITE') && enableTimecardSubmission;

  return (
    <Row style={{ height: '100%', width: '100%' }}>
      {!isFullScreen && (
        <Col span={6} style={{ height: '100%', paddingRight: 10 }}>
          <SideList
            id="team-timecards"
            key="team-timecards"
            header={(
              <SideListSearchHeader
                onChange={onSearch}
                submissionView={
                  showSubmissionHeader && (
                    <OnTraccrRadioGroup
                      value={selectedSubmitView}
                      buttonStyle="solid"
                      onChange={onSubmitViewChange}
                      style={styles.submitRadioGroup}
                    >
                      <Radio.Button value={SUBMISSION_VIEW} style={{ ...styles.submitRadio, flex: 2 }}>
                        Submitted
                        {approvalCount ? <Badge count={approvalCount} style={styles.submitBadge} /> : null}
                      </Radio.Button>
                      <Radio.Button value={DEFAULT_VIEW} style={{ ...styles.submitRadio, flex: 1 }}>
                        All
                      </Radio.Button>
                    </OnTraccrRadioGroup>
                  )
                }
                filterView={(
                  <Popover
                    trigger="click"
                    placement="bottom"
                    content={(
                      <FilterDropdown
                        filters={filters}
                        activeFilters={activeFilters}
                        onFilter={onFilter}
                      />
                    )}
                  >
                    <OnTraccrButton
                      title="Filter"
                      type={filterIsAtDefault ? 'cancel' : 'primary'}
                    />
                  </Popover>
                )}
              />
            )}
            dataSource={filteredUsers}
            headerStyle={{
              height: showSubmissionHeader ? 130 : 100,
            }}
            renderItem={renderItem}
            onClick={onUserSelected}
            selectedItems={new Set([selectedUser])}
            rowHeight={50}
            scrollable
            loadingId={loadingId}
          />
        </Col>
      )}
      <Col span={isFullScreen ? 24 : 18} style={{ height: '100%', paddingLeft: isFullScreen ? 0 : 10 }}>
        {selectedUser && (
          <TimeCardsContainer
            userId={selectedUser?.id === 'summary' ? null : selectedUser.id}
            isApprovals={isApprovals}
            relevantUsers={relevantUsers}
            fullUsers={fullUsers}
            isSummary={selectedUser?.id === 'summary'}
            onDateRangeChange={onDateRangeChange}
          />
        )}
      </Col>
    </Row>
  );
}

TeamTimeCardContainer.propTypes = {
  onUserSelect: PropTypes.func.isRequired,
};

export default TeamTimeCardContainer;
